import { formatGiftDuration } from './giftDuration';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type ExcelJS from 'exceljs';

export type AdministrativeReportColumn = {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'money' | 'date' | 'datetime' | 'duration';
  aggregate?: 'sum';
};

export type AdministrativeReportExportInput = {
  title: string;
  filename: string;
  filters?: Record<string, string | number | null | undefined>;
  columns: AdministrativeReportColumn[];
  rows: Array<Record<string, unknown>>;
  summary?: Record<string, unknown>;
};

const BRAND_BLUE: [number, number, number] = [0, 127, 200];
const BRAND_NAVY: [number, number, number] = [15, 23, 42];
const BRAND_PINK: [number, number, number] = [255, 32, 82];
const LIGHT_BLUE = 'EAF6FC';
const BORDER = 'D9E2EC';

const plainText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(item => String(item ?? '')).join(', ');
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

export const safeReportCell = (value: unknown): string | number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : '';
  const text = plainText(value);
  return /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
};

export const formatReportValue = (
  value: unknown,
  column?: AdministrativeReportColumn,
): string | number => {
  if (value === null || value === undefined) return '';
  if (column?.type === 'duration') return formatGiftDuration(Number(value) / 60);
  if (column?.type === 'date') {
    const text = String(value);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : text;
  }
  if (column?.type === 'datetime') {
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime())
      ? String(value)
      : parsed.toLocaleString('es-BO', { timeZone: 'America/La_Paz' });
  }
  return typeof value === 'number' ? value : plainText(value);
};

const printable = (value: unknown, column?: AdministrativeReportColumn): string => {
  const formatted = formatReportValue(value, column);
  return typeof formatted === 'number' ? String(formatted) : formatted;
};

const excelValue = (value: unknown, column: AdministrativeReportColumn): string | number => {
  if (column.type === 'money' || column.type === 'number') {
    const parsed = typeof value === 'number' ? value : Number(String(value ?? '').replace(',', '.'));
    if (Number.isFinite(parsed)) return parsed;
  }
  return safeReportCell(formatReportValue(value, column));
};

export const createAdministrativeReportPdf = (
  input: AdministrativeReportExportInput,
): jsPDF => {
  const document = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const width = document.internal.pageSize.getWidth();

  document.setFillColor(...BRAND_BLUE);
  document.rect(0, 0, width, 5, 'F');
  document.setFont('helvetica', 'bold');
  document.setFontSize(17);
  document.setTextColor(...BRAND_NAVY);
  document.text(input.title, 14, 17);
  document.setFont('helvetica', 'normal');
  document.setFontSize(8);
  document.setTextColor(100, 116, 139);
  document.text(`Generado: ${new Date().toLocaleString('es-BO', { timeZone: 'America/La_Paz' })}`, 14, 23);

  let cursorY = 29;
  const filters = Object.entries(input.filters || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined);
  if (filters.length) {
    document.setFontSize(8);
    document.setTextColor(...BRAND_NAVY);
    const filterText = filters.map(([key, value]) => `${key}: ${printable(value)}`).join('  |  ');
    const lines = document.splitTextToSize(filterText, width - 28);
    document.text(lines, 14, cursorY);
    cursorY += lines.length * 4 + 2;
  }

  const summary = Object.entries(input.summary || {});
  if (summary.length) {
    document.setFillColor(248, 250, 252);
    document.roundedRect(14, cursorY, width - 28, 10, 2, 2, 'F');
    document.setFont('helvetica', 'bold');
    document.setTextColor(...BRAND_PINK);
    document.text(summary.map(([key, value]) => `${key}: ${printable(value)}`).join('   ·   '), 18, cursorY + 6.5, { maxWidth: width - 36 });
    cursorY += 15;
  }

  autoTable(document, {
    startY: cursorY,
    head: [input.columns.map(column => column.label)],
    body: input.rows.map(row => input.columns.map(column => printable(row[column.key], column))),
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 7, cellPadding: 2, overflow: 'linebreak', lineColor: [226, 232, 240], lineWidth: 0.15 },
    headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 14, right: 14, top: 15, bottom: 13 },
    showHead: 'everyPage',
    didDrawPage: data => {
      document.setFontSize(7);
      document.setTextColor(148, 163, 184);
      document.text(`Página ${data.pageNumber}`, width - 14, document.internal.pageSize.getHeight() - 7, { align: 'right' });
    },
  });

  return document;
};

export const createAdministrativeReportWorkbook = async (
  input: AdministrativeReportExportInput,
): Promise<ExcelJS.Workbook> => {
  const { default: ExcelJSRuntime } = await import('exceljs');
  const workbook = new ExcelJSRuntime.Workbook();
  workbook.creator = 'Co Marca';
  workbook.created = new Date();
  const sheet = workbook.addWorksheet('Reporte', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, paperSize: 9 },
    properties: { defaultRowHeight: 18 },
  });

  sheet.mergeCells(1, 1, 1, Math.max(input.columns.length, 1));
  const title = sheet.getCell(1, 1);
  title.value = safeReportCell(input.title);
  title.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF007FC8' } };
  title.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.getRow(1).height = 28;

  let rowNumber = 3;
  const filters = Object.entries(input.filters || {}).filter(([, value]) => value !== '' && value !== null && value !== undefined);
  filters.forEach(([key, value]) => {
    sheet.getCell(rowNumber, 1).value = safeReportCell(key);
    sheet.getCell(rowNumber, 1).font = { name: 'Arial', bold: true, color: { argb: 'FF475569' } };
    sheet.getCell(rowNumber, 2).value = safeReportCell(value);
    rowNumber += 1;
  });

  const summary = Object.entries(input.summary || {});
  if (filters.length && summary.length) rowNumber += 1;
  summary.forEach(([key, value]) => {
    sheet.getCell(rowNumber, 1).value = safeReportCell(key);
    sheet.getCell(rowNumber, 1).font = { name: 'Arial', bold: true, color: { argb: 'FFFF2052' } };
    sheet.getCell(rowNumber, 2).value = safeReportCell(value);
    rowNumber += 1;
  });
  if (filters.length || summary.length) rowNumber += 1;

  const headerRowNumber = rowNumber;
  const header = sheet.getRow(headerRowNumber);
  header.values = input.columns.map(column => safeReportCell(column.label));
  header.height = 24;
  header.eachCell(cell => {
    cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF007FC8' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = { bottom: { style: 'thin', color: { argb: `FF${BORDER}` } } };
  });

  input.rows.forEach((row, index) => {
    const outputRow = sheet.addRow(input.columns.map(column => excelValue(row[column.key], column)));
    outputRow.font = { name: 'Arial', size: 10, color: { argb: 'FF0F172A' } };
    outputRow.alignment = { vertical: 'top', wrapText: true };
    if (index % 2 === 1) {
      outputRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${LIGHT_BLUE}` } };
      });
    }
  });

  input.columns.forEach((column, index) => {
    const values = [column.label, ...input.rows.slice(0, 200).map(row => printable(row[column.key], column))];
    const longest = Math.max(...values.map(value => value.length), 8);
    sheet.getColumn(index + 1).width = Math.min(Math.max(longest + 2, 12), column.type === 'money' || column.type === 'number' ? 18 : 42);
    if (column.type === 'money') sheet.getColumn(index + 1).numFmt = '#,##0.00;[Red]-#,##0.00;-';
    if (column.type === 'number') sheet.getColumn(index + 1).numFmt = '#,##0.00';
  });

  sheet.views = [{ state: 'frozen', ySplit: headerRowNumber, activeCell: `A${headerRowNumber + 1}` }];
  if (input.columns.length) {
    sheet.autoFilter = {
      from: { row: headerRowNumber, column: 1 },
      to: { row: headerRowNumber, column: input.columns.length },
    };
  }
  sheet.pageSetup.printTitlesRow = `${headerRowNumber}:${headerRowNumber}`;
  sheet.pageSetup.margins = { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 };
  return workbook;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const exportAdministrativeReportPdf = (input: AdministrativeReportExportInput) => {
  createAdministrativeReportPdf(input).save(`${input.filename}.pdf`);
};

export const exportAdministrativeReportExcel = async (input: AdministrativeReportExportInput) => {
  const workbook = await createAdministrativeReportWorkbook(input);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${input.filename}.xlsx`,
  );
};

